// O contrato da folha de laboratório e a geometria do mini-gráfico.
//
// Sem React de propósito: a tela (SVG) e o PDF (canvas do pdfmake) desenham a
// MESMA curva a partir destes números, e o teste do PDF roda fora do navegador.
import { lerMarkdown, type Bloco } from '@medicov1/shared';
import type { PacienteNoPapel } from './identificacao';

export type ForaDaFaixa = 'alto' | 'baixo' | 'dentro' | 'desconhecido';

export interface PontoDTO { valor: number; em: string }

export interface LinhaDTO {
  chave: string;
  rotulo: string;
  unidade: string;
  referencia: { min: number; max: number } | null;
  ultimo: PontoDTO & { texto: string };
  anterior: (PontoDTO & { texto: string }) | null;
  situacao: ForaDaFaixa;
  direcao: 'subiu' | 'caiu' | 'estavel' | null;
  diasDesde: number;
  serie: PontoDTO[];
}

export interface CulturaDTO {
  material: string;
  coletadoEm: string;
  resultado: string | null;
  sensibilidade: string | null;
  situacao: string;
  explicacao: string;
  pergunta: string;
  peso: 'aguardando' | 'tranquilizador' | 'atencao' | 'neutro';
}

export interface FolhaDeLaboratorioDTO {
  gerado: { em: string };
  /** Vai para o médico: identifica como o hospital (`identificar`). */
  paciente: PacienteNoPapel;
  /** "Exames do dia" (01/10): a coleta escolhida (AAAA-MM-DD), ou null na folha de sempre. */
  dia?: string | null;
  /** Os dias com coleta, do mais recente, com quantos resultados. */
  coletas?: { dia: string; quantidade: number }[];
  grupos: { rotulo: string; linhas: LinhaDTO[] }[];
  /** Toda cultura, da mais recente para a mais antiga, com a leitura do resultado (30/09). */
  culturas?: CulturaDTO[];
  vigiar: { tipo: 'remedio' | 'fora' | 'cultura'; texto: string }[];
  leitura: { titulo: string; texto: string; geradoEm: string; autor: string } | null;
}

export const SETA: Record<NonNullable<LinhaDTO['direcao']>, string> = {
  subiu: '↑', caiu: '↓', estavel: '→',
};

/** O que cada situação quer dizer, em palavra — o PDF não tem todos os glifos. */
export const ROTULO_SITUACAO: Record<ForaDaFaixa, string> = {
  alto: 'acima', baixo: 'abaixo', dentro: 'na faixa', desconhecido: '',
};

/**
 * Onde cada ponto cai numa caixa de `largura` x `altura`.
 *
 * A faixa de referência entra na escala: sem ela, uma curva que variou pouco
 * ocuparia a caixa inteira e pareceria um desastre. Com ela, o olho vê de
 * imediato se a linha está dentro ou fora da faixa verde.
 */
export function geometriaDaCurva(
  serie: PontoDTO[],
  referencia: { min: number; max: number } | null,
  largura: number,
  altura: number,
): {
  pontos: { x: number; y: number }[];
  faixa: { y: number; h: number } | null;
} {
  if (serie.length === 0) return { pontos: [], faixa: null };
  const valores = serie.map((p) => p.valor);
  let min = Math.min(...valores, ...(referencia ? [referencia.min] : []));
  let max = Math.max(...valores, ...(referencia ? [referencia.max] : []));
  if (max === min) { max += 1; min -= 1; }
  const folga = (max - min) * 0.1;
  min -= folga; max += folga;
  const y = (v: number) => altura - ((v - min) / (max - min)) * altura;

  // Eixo X pelo TEMPO, não pela ordem: três coletas num dia e uma na semana
  // seguinte não podem aparecer igualmente espaçadas.
  const t0 = new Date(serie[0]!.em).getTime();
  const t1 = new Date(serie[serie.length - 1]!.em).getTime();
  const x = (em: string) => (t1 === t0 ? largura / 2 : ((new Date(em).getTime() - t0) / (t1 - t0)) * largura);

  const pontos = serie.map((p) => ({ x: x(p.em), y: y(p.valor) }));
  const faixa = referencia
    ? (() => {
      const topo = Math.max(0, y(referencia.max));
      const base = Math.min(altura, y(referencia.min));
      return base > topo ? { y: topo, h: base - topo } : null;
    })()
    : null;
  return { pontos, faixa };
}

/**
 * A leitura escrita se divide em duas: o primeiro parágrafo é o resumo, que vai
 * no alto da folha; o resto (correlações, pedidos) vai no fim.
 */
export function dividirLeitura(texto: string): { resumo: Bloco | null; resto: Bloco[] } {
  const blocos = lerMarkdown(texto);
  if (blocos[0]?.tipo === 'paragrafo') return { resumo: blocos[0], resto: blocos.slice(1) };
  return { resumo: null, resto: blocos };
}
